/**
 * 회원 탈퇴(`DELETE /v1/users/me`) 실패의 정규화·분류 — 계약 SoT 는 backend-api Skill 라이브 조회
 * (delete_me_v1_users_me_delete) 와 dronesquare-backend `users/me/domain.py` 의 `delete_self`:
 * - 204: 탈퇴 완료(이미 끝난 탈퇴를 다시 불러도 204).
 * - 409: 대개 삭제되지 않은 소유 프로젝트가 있어 거절된 것(서버 가드 has_active_owned_project: owner_id 이고 is_deleted=false —
 *   진행/완료 상태는 없다)이지만, 같은 409 가 DB 잠금 경합(55P03·40P01·40001)에도 온다 — 그래서 원인은 서버 문구(`message`)만
 *   말하고 화면은 그 문구를 그대로 보인다.
 * - 400 "Cognito 사용자 삭제 실패: …": DB 는 이미 지워졌고 로그인 계정 삭제만 남았다 — 다시 부르면 204.
 * - 400 "유저를 삭제하지 못했습니다.", 5xx, 네트워크: 아무것도 바뀌지 않았다 — 일반 오류.
 * i18n 문구 선택은 화면이 한다(여기엔 t() 가 없다).
 */

/** Amplify REST 오류를 HTTP status 와 wire message 로 정규화한 것 — repository 경계가 던진다. */
export class AccountDeletionError extends Error {
  constructor(readonly status: number | undefined, message: string) {
    super(message);
    this.name = "AccountDeletionError";
  }
}

const HTTP_BAD_REQUEST = 400;
const HTTP_CONFLICT = 409;
/** 백엔드가 IdP 삭제 실패를 알리는 message 접두 — 이 뒤에는 boto3 오류 문자열이 붙는다. */
const COGNITO_DELETE_FAILED_PREFIX = "Cognito 사용자 삭제 실패";

const parseJsonOrNull = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

/**
 * 오류 본문의 wire message — `message`, 없으면 `detail`. Amplify 는 본문을 문자열로 주므로 JSON 으로 읽고,
 * 이미 객체면 그대로 본다. 본문이 JSON 이 아니거나 그 값이 문자열이 아니면 undefined 다(그래도 status 기반
 * 분기는 유지된다).
 */
const readBodyMessage = (body: unknown): string | undefined => {
  const fields = typeof body === "string" ? parseJsonOrNull(body) : body;
  if (!fields || typeof fields !== "object") return undefined;
  const { message, detail } = fields as { message?: unknown; detail?: unknown };
  const text = message ?? detail;
  return typeof text === "string" ? text : undefined;
};

/**
 * Amplify v6 REST 오류(`RestApiError`)의 모양은 한 가지가 아니다 — `response.statusCode` · `response.status` ·
 * `$metadata.httpStatusCode` 세 축을 모두 본다(`walkSessionErrorStatus` 선례). message 는 본문 문구
 * (`readBodyMessage`)가 먼저고, 그것이 없거나 비었으면 오류 자체의 `message`, 그마저 없으면 빈 문자열이다.
 */
export const toAccountDeletionError = (error: unknown): AccountDeletionError => {
  if (error instanceof AccountDeletionError) return error;
  const candidate = error as {
    response?: { status?: number; statusCode?: number; body?: unknown };
    $metadata?: { httpStatusCode?: number };
    message?: string;
  } | null;
  const status =
    candidate?.response?.statusCode ??
    candidate?.response?.status ??
    candidate?.$metadata?.httpStatusCode;
  const bodyMessage = readBodyMessage(candidate?.response?.body);
  const ownMessage = typeof candidate?.message === "string" ? candidate.message : "";
  return new AccountDeletionError(status, bodyMessage || ownMessage);
};

export type AccountDeletionFailure =
  /**
   * 409 — 서버가 거절했다. 대개 삭제되지 않은 소유 프로젝트가 있어서이지만 DB 잠금 경합도 같은 409 라, kind 는 원인을
   * 보증하지 않고 `message`(서버 문구 그대로)가 원인을 말한다.
   */
  | { kind: "ownedProject"; message: string }
  /** 400 "Cognito 사용자 삭제 실패" — 계정 데이터는 지워졌고 로그인 계정 삭제만 남았다. 다시 부르면 끝난다. */
  | { kind: "retry" }
  /** 그 밖 — 아무것도 바뀌지 않았다. */
  | { kind: "generic" };

/** 탈퇴 실패를 화면이 안내할 세 갈래로 가른다. */
export const resolveAccountDeletionFailure = (error: unknown): AccountDeletionFailure => {
  const normalized = toAccountDeletionError(error);
  if (normalized.status === HTTP_CONFLICT) {
    return { kind: "ownedProject", message: normalized.message };
  }
  if (
    normalized.status === HTTP_BAD_REQUEST &&
    normalized.message.startsWith(COGNITO_DELETE_FAILED_PREFIX)
  ) {
    return { kind: "retry" };
  }
  return { kind: "generic" };
};
